import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import Swal from "sweetalert2";
import {
  ArrowLeft,
  Building2,
  Clock3,
  CreditCard,
  MapPin,
  Package,
  Phone,
  Truck,
  User,
} from "lucide-react";
import { GetOrdersByIdApi, GetOrdersVendorByIdApi, MyAccounts } from "../../common/CommonApi";

const statusSteps = ["pending", "processing", "confirmed", "shipped", "delivered"];

const statusLabels = {
  pending: "New",
  processing: "Processing",
  confirmed: "Ready To Ship",
  packed: "Packed",
  shipped: "In Transit",
  out_for_delivery: "In Transit",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const formatMoney = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const formatDate = (value) => {
  if (!value) return "Not available";
  return new Date(value).toLocaleString();
};

const fullAddress = (address) => {
  if (!address) return "No address found";
  return [
    address.houseNoTowerBlock,
    address.addressStreetArea,
    address.localityTown,
    address.cityDistict,
    address.state,
    address.pincode,
  ]
    .filter(Boolean)
    .join(", ");
};

const pickVendor = (order, item) =>
  order.vendor || item?.vendor || item?.product?.vendor || order.product?.vendor || {};

const pickProduct = (order, item) => item?.product || order.product || {};

const pickVariant = (order, item, product) => item?.variant || order.variant || product.variant || {};

const getDisplayOrderId = (order) => order?.sourceOrderId || order?._id;

const ViewOrder = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [orderDetails, setOrderDetails] = useState(null);
  const [account, setAccount] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentAccount = await MyAccounts();
        setAccount(currentAccount);
        const response = currentAccount?.isVendor
          ? await GetOrdersVendorByIdApi(id)
          : await GetOrdersByIdApi(id);

        if (response?.data?.message === "DATA_FOUND") {
          setOrderDetails(response.data.data?.[0]);
        } else {
          Swal.fire("Error", "Order not found", "error");
        }
      } catch (error) {
        console.error("Unable to load order details:", error);
        Swal.fire("Session Expired", "Please login again.", "warning");
        navigate("/login");
      }
    };

    fetchData();
  }, [id, navigate]);

  const items = useMemo(() => orderDetails?.items || [], [orderDetails]);
  const firstItem = items[0] || {};
  const vendor = pickVendor(orderDetails || {}, firstItem);
  const address = orderDetails?.addressInfo;
  const currentStatus = orderDetails?.status || "pending";
  const trackerStatus = currentStatus === "out_for_delivery" ? "shipped" : currentStatus;
  const currentStep = statusSteps.indexOf(trackerStatus);
  const displayOrderId = getDisplayOrderId(orderDetails);
  const timeline = orderDetails?.timeline?.length
    ? [...orderDetails.timeline].sort((a, b) => new Date(b.date) - new Date(a.date))
    : [
        {
          status: currentStatus,
          description: statusLabels[currentStatus] || currentStatus,
          date: orderDetails?.createdDate,
        },
      ];

  if (!orderDetails) {
    return <Loading>Loading order details...</Loading>;
  }

  return (
    <Page>
      <Header>
        <BackButton onClick={() => navigate("/orders")}>
          <ArrowLeft size={18} />
          Back
        </BackButton>
        <div>
          <Title>Order #{displayOrderId?.slice(-8)?.toUpperCase()}</Title>
          <SubTitle>{formatDate(orderDetails.createdDate)}</SubTitle>
        </div>
        <HeaderActions>
          {!account?.isVendor && (
            <PrimaryButton onClick={() => navigate(`/orders/status/?id=${orderDetails._id}`)}>
              <Truck size={17} />
              Update Tracking
            </PrimaryButton>
          )}
        </HeaderActions>
      </Header>

      <SummaryGrid>
        <SummaryItem>
          <CreditCard size={20} />
          <div>
            <span>Total Amount</span>
            <strong>{formatMoney(orderDetails.totalAmount || orderDetails.finalAmount || firstItem.price)}</strong>
          </div>
        </SummaryItem>
        <SummaryItem>
          <Package size={20} />
          <div>
            <span>Items</span>
            <strong>{items.length || 1}</strong>
          </div>
        </SummaryItem>
        <SummaryItem>
          <Clock3 size={20} />
          <div>
            <span>Status</span>
            <strong>{statusLabels[currentStatus] || currentStatus}</strong>
          </div>
        </SummaryItem>
        <SummaryItem>
          <CreditCard size={20} />
          <div>
            <span>Payment</span>
            <strong>{orderDetails.paymentMethod || "Not available"}</strong>
          </div>
        </SummaryItem>
      </SummaryGrid>

      <ContentGrid>
        <MainColumn>
          <Panel>
            <PanelTitle>
              <Package size={19} />
              Product Details
            </PanelTitle>
            {(items.length ? items : [firstItem]).map((item, index) => {
              const itemProduct = pickProduct(orderDetails, item);
              const itemVariant = pickVariant(orderDetails, item, itemProduct);
              const images =
                itemVariant.images ||
                itemVariant.varientImages ||
                itemProduct.images ||
                [];

              return (
                <ProductBlock key={itemProduct._id || index}>
                  <ImageStrip>
                    {(images.length ? images : ["/assets/defaultimage.png"]).slice(0, 4).map((image, imageIndex) => (
                      <ProductImage key={`${image}-${imageIndex}`} src={image} alt={itemProduct.name || "Product"} />
                    ))}
                  </ImageStrip>
                  <ProductInfo>
                    <h3>{itemProduct.name || item.productName || orderDetails.name || "Product not found"}</h3>
                    <p>{itemProduct.shortDescription || itemProduct.description || "No product description available"}</p>
                    <DetailGrid>
                      <Detail label="Quantity" value={item.quantity || orderDetails.quantity || 1} />
                      <Detail label="Price" value={formatMoney(item.price || orderDetails.price)} />
                      <Detail label="Size" value={itemVariant.size || item.variantSize || orderDetails.size} />
                      <Detail label="Color" value={itemVariant.color || item.variantColor || orderDetails.color} />
                      <Detail label="SKU" value={item.sku || itemVariant.sku} />
                      <Detail label="Pickup" value={itemProduct.pickup} />
                      <Detail label="Material" value={itemProduct.material || itemVariant.material} />
                      <Detail label="HSN / Tax Code" value={itemProduct.HsnTaxCode} />
                    </DetailGrid>
                  </ProductInfo>
                </ProductBlock>
              );
            })}
          </Panel>

          <Panel>
            <PanelTitle>
              <Truck size={19} />
              Tracking
            </PanelTitle>
            <Tracker>
              {statusSteps.map((step, index) => {
                const isActive = currentStatus === "cancelled" ? false : index <= currentStep;
                return (
                  <TrackStep key={step} active={isActive}>
                    <span>{index + 1}</span>
                    <strong>{statusLabels[step]}</strong>
                  </TrackStep>
                );
              })}
            </Tracker>
            <Timeline>
              {timeline.map((event, index) => (
                <TimelineItem key={`${event.status}-${index}`}>
                  <TimelineDot danger={event.status === "cancelled"} />
                  <div>
                    <strong>{statusLabels[event.status] || event.status}</strong>
                    <p>{event.description || "Status updated"}</p>
                    <span>{formatDate(event.date)}</span>
                  </div>
                </TimelineItem>
              ))}
            </Timeline>
          </Panel>
        </MainColumn>

        <SideColumn>
          <Panel>
            <PanelTitle>
              <User size={19} />
              User Details
            </PanelTitle>
            <InfoList>
              <InfoRow label="User ID" value={orderDetails.user?._id || orderDetails.userId} />
              <InfoRow label="Phone" value={orderDetails.user?.phoneNumber} icon={<Phone size={16} />} />
              <InfoRow label="Receiver" value={address?.receiversName} />
              <InfoRow label="Receiver Phone" value={address?.receiversPhone} />
            </InfoList>
          </Panel>

          <Panel>
            <PanelTitle>
              <Building2 size={19} />
              Vendor Details
            </PanelTitle>
            <InfoList>
              <InfoRow label="Name" value={vendor.companyName || vendor.fullname || vendor.legalName} />
              <InfoRow label="Email" value={vendor.email} />
              <InfoRow label="Phone" value={vendor.phone} />
              <InfoRow label="GST" value={vendor.gst} />
              <InfoRow label="Address" value={vendor.registeredAddress || vendor.address} />
            </InfoList>
          </Panel>

          <Panel>
            <PanelTitle>
              <MapPin size={19} />
              Delivery Address
            </PanelTitle>
            <AddressCard>
              <strong>{address?.receiversName || "Receiver not available"}</strong>
              <span>{address?.receiversPhone}</span>
              <p>{fullAddress(address)}</p>
              <small>{address?.typeOfAddress || "delivery"}</small>
            </AddressCard>
          </Panel>

          <Panel>
            <PanelTitle>Payment Details</PanelTitle>
            <InfoList>
              <InfoRow label="Method" value={orderDetails.paymentMethod} />
              <InfoRow label="Payment Status" value={orderDetails.paymentStatus} />
              <InfoRow label="Transaction ID" value={orderDetails.transactionId} />
              <InfoRow label="Final Amount" value={formatMoney(orderDetails.finalAmount || orderDetails.totalAmount)} />
            </InfoList>
          </Panel>

          {!account?.isVendor && orderDetails.priceSummary && (
            <Panel>
              <PanelTitle>
                <CreditCard size={19} />
                Price Summary
              </PanelTitle>
              <InfoList>
                <InfoRow label="Item Amount" value={formatMoney(orderDetails.priceSummary.itemAmount)} />
                {/* <InfoRow label="Product Discount" value={formatMoney(orderDetails.priceSummary.productDiscountAmount)} /> */}
                {/* <InfoRow label="Discounted Item Amount" value={formatMoney(orderDetails.priceSummary.discountedItemAmount)} /> */}
                <InfoRow label="Coupon Code" value={orderDetails.priceSummary.couponCode} />
                <InfoRow label="Coupon Discount" value={formatMoney(orderDetails.priceSummary.couponDiscount)} />
                <InfoRow label="Total Discount" value={formatMoney(orderDetails.priceSummary.discountAmount)} />
                <InfoRow label="Redeem Cash" value={formatMoney(orderDetails.priceSummary.redeemCash)} />
                <InfoRow label="Cashback Used" value={formatMoney(orderDetails.priceSummary.cashbackAmount)} />
                <InfoRow label="Product Payable Amount" value={formatMoney(orderDetails.priceSummary.productPayableAmount)} />
                <InfoRow label="Delivery Charge" value={formatMoney(orderDetails.priceSummary.deliveryCharge)} />
                <InfoRow label="Platform Charge" value={formatMoney(orderDetails.priceSummary.platformCharge)} />
                <InfoRow label="Try & Buy Charge" value={formatMoney(orderDetails.priceSummary.tryNBuyCharge)} />
                <InfoRow label="Total Charges" value={formatMoney(orderDetails.priceSummary.totalCharges)} />
                <InfoRow label="Payable Before Refunds" value={formatMoney(orderDetails.priceSummary.payableBeforeRefunds)} />
                <InfoRow label="Refunds" value={formatMoney(orderDetails.priceSummary.refunds)} />
                <InfoRow label="Payable After Refunds" value={formatMoney(orderDetails.priceSummary.payableAfterRefunds)} />
                <InfoRow label="Total Amount" value={formatMoney(orderDetails.priceSummary.totalAmount)} />
                <InfoRow label="Final Amount" value={formatMoney(orderDetails.priceSummary.finalAmount)} />
                <InfoRow label="Remaining Amount" value={formatMoney(orderDetails.priceSummary.remainingAmount)} />
              </InfoList>
            </Panel>
          )}
        </SideColumn>
      </ContentGrid>
    </Page>
  );
};

export default ViewOrder;

const Detail = ({ label, value }) => (
  <DetailItem>
    <span>{label}</span>
    <strong>{value || "Not available"}</strong>
  </DetailItem>
);

const InfoRow = ({ label, value, icon }) => (
  <InfoItem>
    <span>{label}</span>
    <strong>
      {icon}
      {value || "Not available"}
    </strong>
  </InfoItem>
);

const Loading = styled.div`
  color: #64748b;
  padding: 24px;
`;

const Page = styled.div`
  color: #172033;
`;

const Header = styled.div`
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: auto 1fr auto;
  margin-bottom: 20px;

  @media (max-width: 640px) {
    grid-template-columns: auto 1fr;

    > :last-child {
      grid-column: 1 / -1;
    }
  }
`;

const BackButton = styled.button`
  align-items: center;
  background: #ffffff;
  border: 1px solid #dbe3ef;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  font-weight: 700;
  gap: 8px;
  padding: 10px 12px;
`;

const Title = styled.h2`
  font-size: 22px;
  margin: 0 0 4px;
`;

const SubTitle = styled.div`
  color: #64748b;
  font-size: 13px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 640px) {
    width: 100%;

    button {
      justify-content: center;
      width: 100%;
    }
  }
`;

const PrimaryButton = styled.button`
  align-items: center;
  background: #6d4de0;
  border: 0;
  border-radius: 6px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  font-weight: 800;
  gap: 8px;
  padding: 11px 16px;
`;

const SummaryGrid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(4, minmax(160px, 1fr));
  margin-bottom: 18px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const SummaryItem = styled.div`
  align-items: center;
  background: #ffffff;
  border: 1px solid #e8eef5;
  border-radius: 8px;
  display: flex;
  gap: 12px;
  padding: 16px;

  svg {
    color: #6d4de0;
  }

  span {
    color: #64748b;
    display: block;
    font-size: 12px;
    margin-bottom: 4px;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1fr) 360px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const MainColumn = styled.div`
  display: grid;
  gap: 18px;
`;

const SideColumn = styled.div`
  display: grid;
  gap: 18px;
`;

const Panel = styled.section`
  background: #ffffff;
  border: 1px solid #e8eef5;
  border-radius: 8px;
  padding: 18px;

  @media (max-width: 480px) {
    padding: 14px;
  }
`;

const PanelTitle = styled.h3`
  align-items: center;
  display: flex;
  font-size: 16px;
  gap: 8px;
  margin: 0 0 16px;
`;

const ProductBlock = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: 96px 1fr;

  & + & {
    border-top: 1px solid #eef2f7;
    margin-top: 18px;
    padding-top: 18px;
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const ImageStrip = styled.div`
  display: grid;
  gap: 8px;

  @media (max-width: 560px) {
    display: flex;
    overflow-x: auto;
    padding-bottom: 4px;
  }
`;

const ProductImage = styled.img`
  aspect-ratio: 1;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  object-fit: cover;
  width: 84px;
  flex: 0 0 84px;
`;

const ProductInfo = styled.div`
  h3 {
    font-size: 18px;
    margin: 0 0 8px;
  }

  p {
    color: #64748b;
    line-height: 1.5;
    margin: 0 0 14px;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(4, minmax(120px, 1fr));

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  background: #f8fafc;
  border-radius: 6px;
  padding: 10px;

  span {
    color: #64748b;
    display: block;
    font-size: 12px;
    margin-bottom: 4px;
  }

  strong {
    font-size: 14px;
  }
`;

const Tracker = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(5, 1fr);
  margin-bottom: 20px;

  @media (max-width: 560px) {
    grid-template-columns: repeat(5, minmax(70px, 1fr));
    overflow-x: auto;
    padding-bottom: 10px;
  }
`;

const TrackStep = styled.div`
  align-items: center;
  color: ${({ active }) => (active ? "#166534" : "#94a3b8")};
  display: grid;
  gap: 8px;
  justify-items: center;
  text-align: center;

  span {
    align-items: center;
    background: ${({ active }) => (active ? "#dcfce7" : "#f1f5f9")};
    border: 1px solid ${({ active }) => (active ? "#86efac" : "#dbe3ef")};
    border-radius: 50%;
    display: flex;
    height: 34px;
    justify-content: center;
    width: 34px;
  }

  strong {
    font-size: 12px;
  }
`;

const Timeline = styled.div`
  border-left: 2px solid #e5e7eb;
  display: grid;
  gap: 16px;
  margin-left: 9px;
  padding-left: 18px;
`;

const TimelineItem = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: auto 1fr;
  position: relative;

  p {
    color: #475569;
    margin: 4px 0;
  }

  span {
    color: #94a3b8;
    font-size: 12px;
  }
`;

const TimelineDot = styled.span`
  background: ${({ danger }) => (danger ? "#dc2626" : "#6d4de0")};
  border: 3px solid #ffffff;
  border-radius: 50%;
  height: 14px;
  left: -26px;
  position: absolute;
  top: 2px;
  width: 14px;
`;

const InfoList = styled.div`
  display: grid;
  gap: 12px;
`;

const InfoItem = styled.div`
  display: grid;
  gap: 4px;

  span {
    color: #64748b;
    font-size: 12px;
  }

  strong {
    align-items: center;
    display: flex;
    font-size: 14px;
    gap: 6px;
    overflow-wrap: anywhere;
  }
`;

const AddressCard = styled.div`
  background: #f8fafc;
  border-radius: 8px;
  display: grid;
  gap: 6px;
  padding: 14px;

  span,
  small {
    color: #64748b;
  }

  p {
    line-height: 1.45;
    margin: 0;
  }
`;
